import React, { useMemo, useState } from 'react';
import {
    Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Select, MenuItem, Chip, Tooltip, TextField, InputAdornment,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Warning, ShowChart, VpnKey, TextFields, Search } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SmartSendColumn, SmartSendTokenInfo } from '../../../Models/DataSources/SmartSend';
import { resolveColumnLabel } from '../columnLabel';
import { suggestMapping } from '../suggestMapping';

// §11.4 · one row per ##token## → a Select over the version's columns (labelled by
// `resolveColumnLabel`, imported from ../columnLabel — DisplayName, but never blank).
// Mapping is by ColumnID (rename-safe). Each token carries exactly one badge: graph /
// system-field / free. Warnings: unmapped (blocks send at M9), a mapped column that
// VANISHED in the locked version (remap), and a system-field token mapped to a source
// (the source value overrides the recipient's account field). Long templates (50 tokens)
// scroll and are searchable; long Hebrew names wrap and carry a title (§ה.6).
//
// NAME-SIMILARITY SUGGESTIONS (`../suggestMapping`, pure module, no redux, no i18n):
// exactly ONE entry point — every unmapped row offers its single best candidate as a clickable
// chip. It goes through the SAME `onChange` a manual pick uses, so nothing new touches redux and
// the screen's setDirty(true) + 750ms debounced autosave (SmartSendScreen.tsx:225-235) treat it
// like any other manual edit.
//
// WHY THERE IS NO LONGER A BULK "AUTO-MAP" BUTTON (three review rounds, removed): the confidence
// score measures NAME similarity, and name similarity is not meaning. `MobilePhone` scored 1.0000
// against a column displayed as "טלפון נייד חסום" (a SUPPRESSION flag) and `EmailAddress` 1.0000
// against "האימייל של בן הזוג" (the spouse's address). Applied in bulk those are persisted by the
// 750ms autosave with no undo and are afterwards indistinguishable from a choice the user made.
// The CHIP is safe where the button was not, and for a structural reason rather than a tuning one:
// `resolveColumnLabel` prints the candidate column's ACTUAL name on the chip, so those same cases
// read "הצעה: טלפון נייד חסום" and the user simply does not click. One row, named, one click.
//
// ✅ 2026-09-28 (owner, stage) — ONE BULK CHIP IS BACK, FOR "USED LAST TIME" ONLY
// (`bulkSuggestionSource`). What the paragraph above removed was bulk NAME SIMILARITY: a guess, and
// in bulk a wrong one. "נבחר בפעם הקודמת" is not a guess. It is the column this sub-account itself
// chose last time for this very token or variable, offered only when that header is in the version
// on screen. So the header offers one click that applies EXACTLY the memory chips on screen. The
// caller names what counts, and it passes memory and nothing else. The similarity chip ("הצעה: X")
// and the positional "כמו במייל" chip stay one row at a time.
//
// WHY A CLICK AND NOT A PRE-FILL: businessColumnDefaults.ts:8-16 records the property this must
// not break — a GUESSED value must never trip the autosave into the shared production DB on its
// own. Defaults therefore live in a reducer (structurally unable to set `dirty`). A suggestion
// computed here CAN set dirty, and that is precisely why it is gated behind a click: the guess
// only ever leaves the component when the user asks for it, one named row at a time.
//
// WHY THE SUGGESTIONS ARE COMPUTED HERE AND NOT IN THE SLICE: they are derived state — a pure
// function of (tokens, columns, value), all three already props. Storing them would add a fourth
// thing to keep in sync and would put a guess into persisted state before anyone acted on it.

// ── SmartSend part B: TEN OPTIONAL props (contract §6, as amended by v1.21) ──────────────────
// Counted, not asserted. The interface below declares **4 required** (`tokens`, `columns`,
// `value`, `onChange`) and **12 optional**; `warnSystemFieldOverride` pre-dates part B
// (`git show HEAD:` has exactly it and nothing else), so **TEN** are part-B additions:
//   renderBadge · headerLabels · renderTokenLabel · extraOptions · onStaticChange ·
//   staticValueOf · isMappedOverride · suggestionSource · emptyLabel · readOnly
// and the twelfth is not part B at all: `fallbackToSimilarity` (08-mapping-memory, 2026-09-23),
// the OPT-IN that lets `suggestionSource` go FIRST instead of REPLACING the name-similarity chip.
// It defaults to `false`, and absent it this component renders exactly what it rendered before.
//
// ⚠️ THIS HEADER SAID "NINE" AND THAT WAS WRONG — it was written before `staticValueOf` was
// added and never recounted, and a reader who trusts a file's own header does not go and count.
// §6 has since been amended to match the code: "**10 props אופציונליים** (🔴 v1.21: היו 9. נוסף
// `staticValueOf?`)" — the reader that §6 names for `onStaticChange`'s writer, without which the
// WhatsApp tab cannot display a saved constant at all.
//
// Every one of them defaults to `undefined`, and with all ten absent this component renders
// BYTE-IDENTICALLY to what it rendered before (the acceptance is an outerHTML diff of the
// e-mail tab). They exist because the WhatsApp variable table is the SAME table with different
// labels and one extra kind of value — forking the component would have produced two tables
// that drift, which is the failure mode this file's own header already warns about.
export interface TokenSuggestion { columnId: number; label?: string }

interface Props {
    tokens: SmartSendTokenInfo[];
    columns: SmartSendColumn[];
    value: { [token: string]: number | null };
    onChange: (token: string, columnId: number | null) => void;
    warnSystemFieldOverride?: boolean;
    /** Replaces the internal `badgeFor`. `null` ⇒ no badge at all (WhatsApp variables are not
     *  graph / system / free — those are e-mail token kinds). `undefined` ⇒ as today. */
    renderBadge?: (tok: SmartSendTokenInfo) => React.ReactNode | null;
    /** Table headers. `undefined` ⇒ `mapping.tokenCol` / `mapping.columnCol` as today. */
    headerLabels?: { token: string; column: string };
    /** How the token name itself is printed — `{{n}}` on the WhatsApp side. */
    renderTokenLabel?: (tok: SmartSendTokenInfo) => React.ReactNode;
    /** Extra entries in the Select, e.g. "ערך קבוע…". Rendered after the real columns. */
    extraOptions?: { value: string; label: string }[];
    /** The static-value TextField reports here. Present ⇒ the field is rendered for a row whose
     *  Select is on an extra option. */
    onStaticChange?: (tok: SmartSendTokenInfo, value: string | null) => void;
    /** Current static value of a row, so the field is controlled by the caller's state. */
    staticValueOf?: (tok: SmartSendTokenInfo) => string | null;
    /** Overrides "is this row mapped" — a row holding a static value counts as mapped for the
     *  row tint, the header counter and the "unmapped only" filter. */
    isMappedOverride?: (tok: SmartSendTokenInfo) => boolean;
    /** Replaces the name-similarity suggestions with a caller-supplied list ("כמו במייל"). */
    suggestionSource?: (tok: SmartSendTokenInfo) => TokenSuggestion | null;
    /** 08-mapping-memory · OPT-IN, default `false`. With `suggestionSource` present, a row the
     *  source answers `null` for falls back to the name-similarity chip instead of offering
     *  nothing — the source goes FIRST rather than replacing it. ONLY the e-mail screen passes it
     *  (memory → similarity). The WhatsApp tab never may: name similarity on `'1'`, `'2'` is the
     *  module that produced 1.0000-confidence wrong columns. Without `suggestionSource` it changes
     *  nothing — similarity is then already the only source. */
    fallbackToSimilarity?: boolean;
    /** 2026-09-28 · the suggestions the header may apply ALL AT ONCE ("מלא הכל כמו בפעם הקודמת").
     *  The caller passes its MEMORY source and nothing else. Never name similarity or the positional
     *  "כמו במייל": those stay one named row at a time (see the header of this file). Absent ⇒ no
     *  chip. Covers the rows on screen (search / "unmapped only" respected) that are still unmapped. */
    bulkSuggestionSource?: (tok: SmartSendTokenInfo) => TokenSuggestion | null;
    /** Text shown instead of the table when there are no tokens. */
    emptyLabel?: string;
    /** 🔴 `true` ⇒ ZERO `onChange` can originate in this component — the Select, the static
     *  TextField AND the suggestion chip. The rule is "no onChange", not "two controls are
     *  disabled": the chip (`:268-290` before this change) is neither a Select nor a TextField,
     *  and the earlier wording left it live inside a tab that had been declared locked
     *  (v1.12 AB-01). Default `undefined`/`false` ⇒ identical render. */
    readOnly?: boolean;
    /** QA BUG-06 · why the table is read-only — the caller's text, shown on hover over each disabled
     *  control (the Select and the static field), and only while `readOnly`. Absent ⇒ identical render. */
    readOnlyReason?: string | null;
}

const useStyles = makeStyles((theme) => ({
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: theme.spacing(1) },
    // The header's right-hand cluster (counter · filter toggle · search).
    // Its own flex box so the header keeps its two-child space-between layout.
    headerActions: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing(1) },
    search: { minWidth: 220 },
    // Viewport-relative, not the old fixed 420px: that cap showed ~6 rows on any screen and made a
    // 50-token template a scroll-inside-a-scroll. A percentage of the viewport grows with the
    // monitor while still keeping the table scrolling INSIDE its box, so the page's own controls
    // (business-columns picker, send bar) never get pushed off the bottom on a long template.
    container: { marginTop: theme.spacing(1), maxHeight: '70vh', border: '1px solid var(--pls-d-border, #e0e0e0)', borderRadius: 6 },
    tokenCell: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap' },
    tokenName: { fontWeight: 600, overflowWrap: 'anywhere' },
    warnRow: { background: 'var(--pls-d-warning-bg, #fdf6ec)' },
    unmapped: { color: 'var(--pls-d-danger, #c0392b)', fontWeight: 600 },
    unmappedCount: { color: 'var(--pls-d-danger, #c0392b)', fontWeight: 600, whiteSpace: 'nowrap' },
    warnIcon: { color: 'var(--pls-d-danger, #c0392b)', cursor: 'help', verticalAlign: 'middle' },
    select: { minWidth: 220 },
    // The status cell now holds up to three things (unmapped text, warning icons, suggestion
    // chip); flex-wrap keeps a long Hebrew column name from stretching the column.
    statusCell: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing(1) },
    suggestChip: { maxWidth: 240 },
}));

const TokenMappingTable: React.FC<Props> = ({
    tokens, columns, value, onChange, warnSystemFieldOverride = true,
    renderBadge, headerLabels, renderTokenLabel, extraOptions, onStaticChange, staticValueOf,
    isMappedOverride, suggestionSource, emptyLabel, readOnly = false, fallbackToSimilarity = false,
    bulkSuggestionSource, readOnlyReason = null,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    // §2.5 · the Select menu portals to document.body — outside App's inner <div dir> — and
    // <html dir> is stuck "ltr", so the dropdown opens LTR. Force its direction like the dialogs.
    const isRTL = useSelector((s: any) => s.core && s.core.isRTL);
    const [search, setSearch] = useState('');
    const [unmappedOnly, setUnmappedOnly] = useState(false);

    const columnSet = useMemo(() => new Set(columns.map((c) => c.ColumnID)), [columns]);
    // ColumnID → the resolved label above, for the suggestion chip. Built once per column list
    // rather than a .find() per row: 50 tokens × 40 columns on every keystroke in the search box.
    // Built from the SAME `columns` array and under the SAME dependency as `columnSet` above, so a
    // ColumnID that passes the columnSet guard at the chip is guaranteed to have an entry here —
    // which is what lets the chip's label read straight out of this map with no undefined case.
    const columnLabel = useMemo(() => {
        const m = new Map<number, string>();
        columns.forEach((c) => m.set(c.ColumnID, resolveColumnLabel(c)));
        return m;
    }, [columns]);

    // THE single definition of "this token still needs a column" — the row tint, the header
    // counter, the "unmapped only" filter and the suggestion chip all call it, so they cannot
    // drift apart. A mapping pointing at a column that VANISHED from the locked version counts as
    // unmapped here because it cannot resolve at send time; that is deliberate and matches what
    // suggestMapping itself treats as unmapped (it re-offers a candidate for such a row).
    const isUnmapped = useMemo(() => (tok: SmartSendTokenInfo) => {
        // `isMappedOverride` is consulted FIRST and it is authoritative. A WhatsApp variable
        // bound to a constant has no ColumnID at all, so the ColumnID test below would call it
        // unmapped and the row tint, the counter and the filter would all contradict a value
        // that is plainly on screen.
        if (isMappedOverride) return !isMappedOverride(tok);
        const m = value[tok.Token] ?? null;
        return !(m != null && m > 0 && columnSet.has(m));
    }, [value, columnSet, isMappedOverride]);

    // Recomputed on `value` as well as on tokens/columns: suggestMapping RESERVES the columns of
    // already-mapped tokens, so mapping one row must stop that column being offered to another.
    // GATE 1 of 2 (08-mapping-memory). Skipped when the caller's own source REPLACES it: that is
    // the WhatsApp tab, whose variables have no name to score — `{{3}}`. Computed when
    // `fallbackToSimilarity` asks for it, because then the caller's source only goes FIRST and a
    // row it has nothing for falls back to this (the e-mail screen: memory → similarity). Without
    // this gate honouring the prop, gate 2 below would fall back to an empty map — inert.
    // ⚠️ Keyed on the BOOLEAN, not on `suggestionSource` itself: only its presence matters here,
    // and a caller passing a fresh arrow each render (both callers do) must not re-run the whole
    // trigram/IDF scoring on every render of the screen. With the prop absent the boolean is
    // exactly `!!suggestionSource`, so both HEAD callers get the same result as before.
    const similarityReplaced = !!suggestionSource && !fallbackToSimilarity;
    const { suggestions } = useMemo(
        () => (similarityReplaced ? { suggestions: {} as any } : suggestMapping(tokens, columns, value)),
        [tokens, columns, value, similarityReplaced],
    );

    // Counted over ALL tokens, never over `filtered` — the header number must not change when the
    // user types in the search box. Cheap enough to do inline (≤ ~50 tokens, §11.4).
    const unmappedCount = tokens.reduce((n, tk) => n + (isUnmapped(tk) ? 1 : 0), 0);

    const filtered = useMemo(() => {
        // Case-insensitive: these are ##token## names typed by hand into the template in whatever
        // case the author felt like, so a case-sensitive indexOf made "email" miss "Email".
        // Hebrew is caseless, so this only ever helps.
        const q = search.trim().toLowerCase();
        let rows = q ? tokens.filter((tk) => tk.Token.toLowerCase().indexOf(q) > -1) : tokens;
        if (unmappedOnly) rows = rows.filter(isUnmapped);
        return rows;
    }, [tokens, search, unmappedOnly, isUnmapped]);

    // NOTHING INTERSECTS THE SUGGESTIONS WITH `filtered` ANY MORE, and nothing should. That
    // intersection existed solely to keep the bulk button from writing rows the search box was
    // hiding; a chip is rendered BY a visible row, so "only acts on what you can see" is now a
    // property of where the control lives rather than a rule some list has to enforce. The only
    // guard a suggestion still needs is the per-row `columnSet` check at the chip itself — the
    // last point before a ColumnID reaches the save request.

    const badgeFor = (tok: SmartSendTokenInfo) => {
        // `renderBadge` may legitimately return null ("no badge"), so the caller is detected by
        // the PROP being present, never by its return value.
        if (renderBadge) return renderBadge(tok);
        if (tok.IsGraphToken) return <Chip size="small" icon={<ShowChart />} label={t('DataSources.send.mapping.badge.graph')} />;
        if (tok.IsSystemField) return <Chip size="small" icon={<VpnKey />} label={t('DataSources.send.mapping.badge.systemField')} />;
        return <Chip size="small" variant="outlined" icon={<TextFields />} label={t('DataSources.send.mapping.badge.free')} />;
    };

    // QA BUG-06 (C-06) — a disabled control fires no mouse events, so the read-only reason hovers on a
    // wrapping span (the SourcePicker / CampaignPicker idiom). Only while `readOnly` and the caller named
    // a reason; otherwise the control renders bare, exactly as before.
    const withReadOnlyReason = (control: React.ReactElement) => (readOnly && readOnlyReason
        ? (
            <Tooltip title={readOnlyReason} PopperProps={{ dir: isRTL ? 'rtl' : 'ltr' }}>
                <span style={{ display: 'inline-flex' }}>{control}</span>
            </Tooltip>
        )
        : control);

    if (!tokens.length) {
        return (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                {emptyLabel ?? t('DataSources.send.mapping.noTokens')}
            </Typography>
        );
    }

    // What the header chip would apply: every row ON SCREEN that is still unmapped and that the
    // caller's memory source answers for, with a column of THIS version — the same three conditions
    // the row's own chip is rendered under. So the chip applies exactly what the operator could have
    // clicked row by row, through the same `onChange`, and nothing a readOnly table may not.
    const bulk = (!readOnly && bulkSuggestionSource)
        ? filtered.reduce<{ token: string; columnId: number }[]>((acc, tok) => {
            if (!isUnmapped(tok)) return acc;
            const s = bulkSuggestionSource(tok);
            if (s && columnSet.has(s.columnId)) acc.push({ token: tok.Token, columnId: s.columnId });
            return acc;
        }, [])
        : [];

    return (
        <Box style={{ marginTop: 24 }}>
            <Box className={classes.header}>
                <Box>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{t('DataSources.send.mapping.title')}</Typography>
                    <Typography variant="body2" color="textSecondary">{t('DataSources.send.mapping.hint')}</Typography>
                </Box>
                <Box className={classes.headerActions}>
                    {unmappedCount > 0 && (
                        <Typography variant="body2" component="span" className={classes.unmappedCount}>
                            {t('DataSources.send.mapping.unmappedCount', { n: unmappedCount.toLocaleString() })}
                        </Typography>
                    )}
                    {/* Rendered while the toggle is ON even with nothing left unmapped: mapping the
                        last field (by chip or by dropdown) would otherwise remove the only control
                        that can turn the filter off, leaving an empty table with no way out. */}
                    {(unmappedCount > 0 || unmappedOnly) && (
                        <Chip
                            size="small"
                            clickable
                            color={unmappedOnly ? 'primary' : 'default'}
                            variant={unmappedOnly ? 'default' : 'outlined'}
                            label={t('DataSources.send.mapping.showUnmappedOnly')}
                            onClick={() => setUnmappedOnly((v) => !v)}
                            aria-pressed={unmappedOnly}
                        />
                    )}
                    {tokens.length > 8 && (
                        <TextField
                            className={classes.search}
                            size="small"
                            variant="outlined"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            // This box filters FIELD names. `DataSources.searchPlaceholder` is the
                            // sources-list string ("search by name or description") and was wrong here.
                            placeholder={t('DataSources.send.mapping.searchPlaceholder')}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                            inputProps={{ 'aria-label': t('DataSources.send.mapping.title') }}
                        />
                    )}
                </Box>
            </Box>

            <TableContainer className={classes.container}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>{headerLabels?.token ?? t('DataSources.send.mapping.tokenCol')}</TableCell>
                            <TableCell>{headerLabels?.column ?? t('DataSources.send.mapping.columnCol')}</TableCell>
                            {/* Above the column of row chips, so it reads as "all of these at once". */}
                            <TableCell>
                                {bulk.length > 0 && (
                                    <Tooltip title={t('DataSources.send.mapping.applyAllMemoryTip')} PopperProps={{ dir: isRTL ? 'rtl' : 'ltr' }}>
                                        <Chip
                                            size="small"
                                            clickable
                                            color="primary"
                                            variant="outlined"
                                            label={t('DataSources.send.mapping.applyAllMemory', { n: bulk.length.toLocaleString() })}
                                            onClick={() => { if (!readOnly) bulk.forEach((b) => onChange(b.token, b.columnId)); }}
                                        />
                                    </Tooltip>
                                )}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.map((tok) => {
                            const mapped = value[tok.Token] ?? null;
                            const isMapped = mapped != null && mapped > 0;
                            const vanished = isMapped && !columnSet.has(mapped as number);
                            // Identical to the previous `isMapped && !vanished`, routed through the
                            // shared helper so the row tint and the header counter can never disagree.
                            const effectivelyMapped = !isUnmapped(tok);
                            const systemOverride = warnSystemFieldOverride && tok.IsSystemField && effectivelyMapped;
                            const rowWarn = !effectivelyMapped;
                            // Offered for a vanished mapping too — that row needs a column just as
                            // much as a never-mapped one. Null unless the candidate belongs to THIS
                            // version's columns, which also guarantees the label below is defined.
                            const sugg = (() => {
                                if (readOnly) return null;      // no onChange may originate here
                                // GATE 2 of 2 (08-mapping-memory). Still picked by the PROP's
                                // presence, and the caller's answer still comes first; only a
                                // `null` from it, and only when the caller opted in, falls back to
                                // the similarity chip. Opted out, `?? null` is exactly the old line.
                                const s = suggestionSource
                                    ? (suggestionSource(tok) ?? (fallbackToSimilarity ? suggestions[tok.Token] : null))
                                    : suggestions[tok.Token];
                                return (!effectivelyMapped && s && columnSet.has(s.columnId)) ? s : null;
                            })();
                            // `resolveColumnLabel` via the map, NOT `DisplayName`: an empty
                            // DisplayName used to render "הצעה: " with nothing after it. The same
                            // resolution feeds the MenuItem below, so the chip and the dropdown can
                            // never disagree about what this column is called.
                            // A caller-supplied `label` is the WHOLE chip text, not a name to
                            // interpolate: the WhatsApp side says "like in the e-mail: X", which
                            // is a different sentence from the e-mail side's "suggestion: X".
                            const suggestLabel = sugg
                                ? ((sugg as any).label
                                    ?? t('DataSources.send.mapping.suggestApply', { name: columnLabel.get(sugg.columnId) }))
                                : '';
                            // Which Select entry this row is on. An extra option (e.g. a constant
                            // value) is a STRING sentinel; a real column is a number. Keeping the
                            // two apart is what lets Number(e.target.value) stay safe below.
                            const staticValue = staticValueOf ? staticValueOf(tok) : null;
                            const onExtra = !!extraOptions?.length && staticValue != null;
                            return (
                                <TableRow key={tok.Token} className={rowWarn ? classes.warnRow : undefined}>
                                    <TableCell>
                                        <Box className={classes.tokenCell}>
                                            <Typography component="span" className={classes.tokenName} title={tok.Token}>
                                                {renderTokenLabel ? renderTokenLabel(tok) : tok.Token}
                                            </Typography>
                                            {badgeFor(tok)}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {withReadOnlyReason(<Select
                                            className={classes.select}
                                            variant="outlined"
                                            margin="dense"
                                            displayEmpty
                                            disabled={readOnly}
                                            value={onExtra ? (extraOptions as any)[0].value : (vanished ? 0 : (mapped ?? 0))}
                                            onChange={(e) => {
                                                if (readOnly) return;
                                                const raw = e.target.value as any;
                                                // An extra option is a string sentinel: switch the
                                                // row to "constant" and clear any column it held,
                                                // so the two sources can never both be set (the
                                                // SP's CK_..._OneSource rejects that outright).
                                                if (typeof raw === 'string' && extraOptions?.some((o) => o.value === raw)) {
                                                    onChange(tok.Token, null);
                                                    if (onStaticChange) onStaticChange(tok, '');
                                                    return;
                                                }
                                                const v = Number(raw);
                                                if (onStaticChange) onStaticChange(tok, null);
                                                onChange(tok.Token, v > 0 ? v : null);
                                            }}
                                            inputProps={{ 'aria-label': `${headerLabels?.column ?? t('DataSources.send.mapping.columnCol')} — ${tok.Token}` }}
                                            MenuProps={{ PaperProps: { dir: isRTL ? 'rtl' : 'ltr' } }}
                                        >
                                            <MenuItem value={0}><em>{t('DataSources.send.mapping.selectColumn')}</em></MenuItem>
                                            {/* Called directly rather than read out of `columnLabel`
                                                so this is literally the same expression the chip's
                                                label came from: a column with a blank DisplayName
                                                was an unpickable blank option here while the chip
                                                offered it by name. */}
                                            {columns.map((c) => <MenuItem key={c.ColumnID} value={c.ColumnID}>{resolveColumnLabel(c)}</MenuItem>)}
                                            {(extraOptions ?? []).map((o) => (
                                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                        </Select>)}
                                        {onExtra && onStaticChange && withReadOnlyReason(
                                            <TextField
                                                style={{ marginTop: 8, minWidth: 220 }}
                                                size="small"
                                                variant="outlined"
                                                disabled={readOnly}
                                                value={staticValue ?? ''}
                                                onChange={(e) => { if (!readOnly) onStaticChange(tok, e.target.value); }}
                                                inputProps={{ 'aria-label': `${(extraOptions as any)[0].label} — ${tok.Token}`, maxLength: 1000 }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Box className={classes.statusCell}>
                                            {/* QA BUG-08-r1 — the same "mapped" as the row tint and the counter
                                                (`effectivelyMapped`, so a typed static value counts), not the
                                                column-only `isMapped`. `!vanished` keeps a vanished column on its
                                                own warning, as before. Without `isMappedOverride` (the e-mail
                                                table) this is exactly the old `!isMapped`. */}
                                            {!effectivelyMapped && !vanished && <span className={classes.unmapped}>{t('DataSources.send.mapping.unmapped')}</span>}
                                            {vanished && (
                                                <Tooltip title={t('DataSources.send.mapping.vanishedColumn')}>
                                                    <Warning className={classes.warnIcon} fontSize="small" />
                                                </Tooltip>
                                            )}
                                            {systemOverride && (
                                                <Tooltip title={t('DataSources.send.mapping.systemFieldWarn')}>
                                                    <Warning className={classes.warnIcon} fontSize="small" />
                                                </Tooltip>
                                            )}
                                            {/* Sits ALONGSIDE the warnings above, never instead of them: the
                                                row is still unmapped until this is clicked. Explicit aria-label
                                                because the visible label names only the COLUMN — out of the row
                                                context a screen reader user cannot tell which field it fills
                                                (same reason as the Select's aria-label above). */}
                                            {sugg && (
                                                <Tooltip title={t('DataSources.send.mapping.suggestTip')} PopperProps={{ dir: isRTL ? 'rtl' : 'ltr' }}>
                                                    <Chip
                                                        size="small"
                                                        clickable
                                                        variant="outlined"
                                                        className={classes.suggestChip}
                                                        label={suggestLabel}
                                                        aria-label={`${suggestLabel} — ${tok.Token}`}
                                                        onClick={() => { if (!readOnly) onChange(tok.Token, sugg.columnId); }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TokenMappingTable;
